import { useEffect, useState } from "react";
import { Download, Link2, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, BASE, errMsg, fetchPdfUrl, LANGS } from "@/lib/api";

export const TEMPLATES = { clinician: "Clinician Doc", patient: "Patient Sheet", doctor: "Doctor Summary" };

export default function PdfPreview({ versionId, refreshKey = 0, compact = false }) {
  const [tpl, setTpl] = useState("clinician");
  const [lang, setLang] = useState("en");
  const [side, setSide] = useState("");
  const [pdf, setPdf] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { setPdf(await fetchPdfUrl(`/pdf/version/${versionId}/${tpl}`, { params: { lang, side } })); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [versionId, tpl, lang, side, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const share = async () => {
    try {
      const { data } = await api.post("/pdf/share", { version_id: versionId, template: tpl, lang, side });
      const url = `${BASE}${data.url}`;
      await navigator.clipboard?.writeText(url).catch(() => {});
      toast.success(`Signed link copied (expires in ${Math.round(data.expires_in / 60)} min)`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2" data-testid="pdf-preview">
      <div className="flex flex-wrap items-center gap-1.5">
        {Object.entries(TEMPLATES).map(([k, l]) => (
          <button key={k} data-testid={`pdf-tab-${k}`} onClick={() => setTpl(k)} className={`rounded px-2.5 py-1 text-[12px] font-semibold ${tpl === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
        ))}
        <select data-testid="pdf-lang-select" value={lang} onChange={(e) => setLang(e.target.value)} className="rounded border border-slate-200 px-1.5 py-1 text-[12px]">
          {Object.entries(LANGS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select data-testid="pdf-side-select" value={side} onChange={(e) => setSide(e.target.value)} className="rounded border border-slate-200 px-1.5 py-1 text-[12px]" title="Figures are mirrored to match the affected side">
          <option value="">Affected side: as protocol</option><option value="right">Affected side: Right</option><option value="left">Affected side: Left</option>
        </select>
        <div className="ml-auto flex gap-1">
          <button data-testid="pdf-refresh-btn" onClick={load} className="p-1.5 rounded hover:bg-slate-100" title="Refresh">{busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}</button>
          {pdf && <a data-testid="pdf-download-btn" href={pdf.url} download={`praniq-${tpl}-${lang}.pdf`} className="p-1.5 rounded hover:bg-slate-100" title="Download"><Download size={14} /></a>}
          <button data-testid="pdf-share-btn" onClick={share} className="p-1.5 rounded hover:bg-slate-100" title="Copy signed expiring link"><Link2 size={14} /></button>
        </div>
      </div>
      <div className="rounded border border-slate-200 bg-slate-100 p-2">
        {pdf ? <iframe title="PDF preview" src={pdf.url} className={`w-full bg-white shadow-md ${compact ? "h-[520px]" : "h-[760px]"}`} data-testid="pdf-iframe" /> : <div className="h-40 grid place-items-center text-sm text-slate-500">Rendering…</div>}
      </div>
      {pdf?.sha && <p className="font-mono text-[10px] text-slate-400 break-all" data-testid="pdf-sha">sha256 {pdf.sha}</p>}
    </div>
  );
}
